/**
 * Entity animation catalog: easing curves and the animatable properties of every target type.
 *
 * One source of truth for the runtime (EntityAnimationSystem) and the editor (timeline,
 * keyframe inspector, the ◆ keyframe buttons in the sidebar), so a property the editor offers
 * is always one the published viewer applies.
 *
 * Values are stored in the units the editor shows, with two historical exceptions kept for
 * saved scenes: transforms use BabylonJS conventions (left-handed, rotation in radians), and
 * colors are stored per channel in 0-1. `displayScale` converts a stored value to the unit a
 * person reads (radians → degrees for rotation).
 */
import type { AnimatableEntityType, AnimatableProperty, AnimationEasingType } from '../types';

// ── Easing ────────────────────────────────────────────────────────────────────────────────

export interface AnimationEasingInfo {
    id: AnimationEasingType;
    label: string;
    description: string;
}

/** Easings in menu order. Unknown easings (a newer scene in an older viewer) play as linear. */
export const ANIMATION_EASINGS: readonly AnimationEasingInfo[] = [
    { id: 'linear', label: 'Linear', description: 'Constant speed.' },
    { id: 'step', label: 'Hold', description: 'Keep this value until the next keyframe, then jump.' },
    { id: 'ease-in', label: 'Ease in', description: 'Start slowly, then speed up.' },
    { id: 'ease-out', label: 'Ease out', description: 'Start quickly, then slow down.' },
    { id: 'ease-in-out', label: 'Ease in-out', description: 'Slow at both ends.' },
    { id: 'ease-in-back', label: 'Anticipate', description: 'Pull back slightly before moving.' },
    { id: 'ease-out-back', label: 'Overshoot', description: 'Go slightly past the target, then settle.' },
    { id: 'ease-in-out-back', label: 'Anticipate + overshoot', description: 'Pull back, then overshoot and settle.' },
    { id: 'ease-out-bounce', label: 'Bounce', description: 'Bounce into place.' },
    { id: 'ease-out-elastic', label: 'Elastic', description: 'Spring past the target and wobble into place.' },
];

const BACK = 1.70158;
const BACK_IN_OUT = BACK * 1.525;

function bounceOut(t: number): number {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) { const u = t - 1.5 / d1; return n1 * u * u + 0.75; }
    if (t < 2.5 / d1) { const u = t - 2.25 / d1; return n1 * u * u + 0.9375; }
    const u = t - 2.625 / d1;
    return n1 * u * u + 0.984375;
}

/**
 * Map linear segment progress t (0-1) through an easing curve. Back/elastic curves leave 0-1
 * on purpose (that is the overshoot); appliers clamp where a property has hard limits.
 */
export function applyAnimationEasing(t: number, easing: AnimationEasingType | string | undefined): number {
    switch (easing) {
        case 'step':
            return 0;
        case 'ease-in':
            return t * t * t;
        case 'ease-out':
            return 1 - (1 - t) * (1 - t) * (1 - t);
        case 'ease-in-out':
            // smoothstep (the curve saved scenes were authored with)
            return t * t * (3 - 2 * t);
        case 'ease-in-back':
            return (BACK + 1) * t * t * t - BACK * t * t;
        case 'ease-out-back': {
            const u = t - 1;
            return 1 + (BACK + 1) * u * u * u + BACK * u * u;
        }
        case 'ease-in-out-back':
            return t < 0.5
                ? ((2 * t) * (2 * t) * ((BACK_IN_OUT + 1) * 2 * t - BACK_IN_OUT)) / 2
                : ((2 * t - 2) * (2 * t - 2) * ((BACK_IN_OUT + 1) * (2 * t - 2) + BACK_IN_OUT) + 2) / 2;
        case 'ease-out-bounce':
            return bounceOut(t);
        case 'ease-out-elastic':
            if (t <= 0) return 0;
            if (t >= 1) return 1;
            return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
        case 'linear':
        default:
            return t;
    }
}

// ── Targets ───────────────────────────────────────────────────────────────────────────────

/**
 * Scene-wide targets: no pc.Entity, one per scene, addressed by a fixed entity id. The viewer
 * applies them through scene handlers (fog, weather, focus/haze regions, post effects, relighting).
 */
export const SCENE_ANIMATION_TARGETS: ReadonlyArray<{ entityType: AnimatableEntityType; entityId: string; name: string }> = [
    { entityType: 'focus', entityId: 'scene.focus', name: 'Focus & Highlights' },
    { entityType: 'haze', entityId: 'scene.haze', name: 'Haze Regions' },
    { entityType: 'postProcessing', entityId: 'scene.postProcessing', name: 'Post Effects' },
    { entityType: 'relighting', entityId: 'scene.relighting', name: 'Splat Relighting' },
    { entityType: 'environment', entityId: 'scene.environment', name: 'Sky & Background' },
    { entityType: 'audioReactive', entityId: 'scene.audioReactive', name: 'Audio Reactivity' },
    { entityType: 'fog', entityId: 'scene.fog', name: 'Scene Fog' },
    { entityType: 'weather', entityId: 'scene.weather', name: 'Scene Weather' },
    { entityType: 'wind', entityId: 'scene.wind', name: 'Scene Wind' },
];

/**
 * Targets applied by a viewer handler instead of a pc.Entity: the scene-wide looks above plus
 * individual focus / haze regions (addressed by region id) and annotation layers (by layer id).
 */
const HANDLER_TARGET_TYPES: ReadonlySet<string> = new Set([...SCENE_ANIMATION_TARGETS.map((t) => t.entityType), 'focusRegion', 'hazeRegion', 'annotationLayer', 'region']);

/** True for targets applied by a scene handler (scene looks, regions), not to a pc.Entity. */
export function isSceneAnimationTarget(entityType: string): boolean {
    return HANDLER_TARGET_TYPES.has(entityType);
}

/** The fixed entity id of a scene-wide target type, e.g. 'focus' → 'scene.focus'. */
export function sceneAnimationTargetId(entityType: string): string | undefined {
    return SCENE_ANIMATION_TARGETS.find((t) => t.entityType === entityType)?.entityId;
}

// ── Property groups ───────────────────────────────────────────────────────────────────────

export type AnimationPropertyKind = 'vector' | 'scalar' | 'color' | 'toggle';

/**
 * A row in the timeline: one or more channels a person thinks of as one thing (Position = x/y/z,
 * Color = r/g/b). Keyframes are stored per channel; the editor keys and moves a group together.
 */
export interface AnimationPropertyGroup {
    id: string;
    label: string;
    kind: AnimationPropertyKind;
    channels: readonly AnimatableProperty[];
    /** Short per-channel labels for vectors ('X', 'Y', 'Z'). */
    channelLabels?: readonly string[];
    /** Inspector hints, in display units. */
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    /** stored value × displayScale = displayed value (rotation: radians → degrees). */
    displayScale?: number;
    /** Part of "Key all" for a fresh animation. Groups that already have keyframes are always keyed. */
    keyByDefault: boolean;
    /** Timeline color. */
    color: string;
}

const C = {
    position: '#4fc3f7',
    rotation: '#aed581',
    scale: '#ffb74d',
    color: '#f06292',
    light: '#ffd54f',
    opacity: '#b39ddb',
    visible: '#90a4ae',
    audio: '#4db6ac',
    particle: '#ff8a65',
    scene: '#81d4fa',
};

const XYZ = ['X', 'Y', 'Z'] as const;
const RGB = ['R', 'G', 'B'] as const;

function vector(id: string, label: string, prefix: string, color: string, extra: Partial<AnimationPropertyGroup> = {}): AnimationPropertyGroup {
    return {
        id, label, kind: 'vector', color, keyByDefault: true,
        channels: [`${prefix}.x`, `${prefix}.y`, `${prefix}.z`] as AnimatableProperty[],
        channelLabels: XYZ,
        ...extra,
    };
}
function colorGroup(id: string, label: string, prefix: string, keyByDefault = true): AnimationPropertyGroup {
    return {
        id, label, kind: 'color', color: C.color, keyByDefault,
        channels: [`${prefix}.r`, `${prefix}.g`, `${prefix}.b`] as AnimatableProperty[],
        channelLabels: RGB, min: 0, max: 1, step: 0.01,
    };
}
function scalar(property: AnimatableProperty, label: string, color: string, extra: Partial<AnimationPropertyGroup> = {}): AnimationPropertyGroup {
    return { id: property, label, kind: 'scalar', color, keyByDefault: true, channels: [property], step: 0.01, ...extra };
}

const POSITION = vector('position', 'Position', 'position', C.position, { step: 0.01 });
const ROTATION = vector('rotation', 'Rotation', 'rotation', C.rotation, { step: 1, unit: '°', displayScale: 180 / Math.PI });
const SCALE = vector('scale', 'Scale', 'scale', C.scale, { step: 0.01 });
const OPACITY = scalar('opacity', 'Opacity', C.opacity, { min: 0, max: 1, keyByDefault: false });
const VISIBLE: AnimationPropertyGroup = {
    id: 'visible', label: 'Visible', kind: 'toggle', color: C.visible, keyByDefault: false, channels: ['visible'], min: 0, max: 1, step: 1,
};
/** Sphere markers only (image / video / GIF markers have no color of their own). */
const MARKER_COLOR = colorGroup('marker.color', 'Sphere color', 'marker.color', false);

const MIRROR_GROUPS: AnimationPropertyGroup[] = [
    scalar('mirror.intensity', 'Reflection strength', C.scene, { min: 0, max: 1, keyByDefault: false }),
    colorGroup('mirror.tint', 'Mirror tint', 'mirror.tint', false),
];

const WATER_GROUPS: AnimationPropertyGroup[] = [
    colorGroup('water.color', 'Water color', 'water.color', false),
    colorGroup('water.deepColor', 'Deep color', 'water.deepColor', false),
    scalar('water.waveStrength', 'Wave strength', C.scene, { min: 0, max: 2, keyByDefault: false }),
    scalar('water.waveSpeed', 'Wave speed', C.scene, { min: 0, max: 5, step: 0.05, keyByDefault: false }),
    scalar('water.waveScale', 'Wave scale', C.scene, { min: 0.1, max: 5, step: 0.05, keyByDefault: false }),
    scalar('water.reflectivity', 'Reflectivity', C.scene, { min: 0, max: 1, keyByDefault: false }),
];

const CLOUD_GROUPS: AnimationPropertyGroup[] = [
    scalar('cloud.coverage', 'Coverage', C.scene, { min: 0, max: 1 }),
    scalar('cloud.density', 'Density', C.scene, { min: 0, max: 1, keyByDefault: false }),
    scalar('cloud.opacity', 'Fade', C.scene, { min: 0, max: 1, keyByDefault: false }),
    scalar('cloud.windSpeed', 'Wind speed', C.scene, { min: 0, step: 0.1, keyByDefault: false }),
    scalar('cloud.windDirection', 'Wind direction', C.scene, { step: 1, unit: '°', keyByDefault: false }),
    scalar('cloud.evolution', 'Billowing', C.scene, { min: 0, max: 1, keyByDefault: false }),
    scalar('cloud.shadowStrength', 'Shadow strength', C.scene, { min: 0, max: 1, keyByDefault: false }),
    colorGroup('cloud.color', 'Sunlit color', 'cloud.color', false),
    colorGroup('cloud.shadeColor', 'Shade color', 'cloud.shadeColor', false),
];

const PARTICLE_GROUPS: AnimationPropertyGroup[] = [
    scalar('particle.rate', 'Emission rate', C.particle, { min: 0, step: 1, unit: '/s' }),
    scalar('particle.lifetime', 'Lifetime', C.particle, { min: 0, step: 0.1, unit: 's', keyByDefault: false }),
    scalar('particle.speed', 'Speed', C.particle, { step: 0.1 }),
    scalar('particle.speed2', 'Speed (max)', C.particle, { step: 0.1, keyByDefault: false }),
    scalar('particle.radialSpeed', 'Radial speed', C.particle, { step: 0.1, keyByDefault: false }),
    scalar('particle.radialSpeed2', 'Radial speed (max)', C.particle, { step: 0.1, keyByDefault: false }),
    scalar('particle.scale', 'Particle size', C.particle, { min: 0, step: 0.01 }),
    scalar('particle.scale2', 'Particle size (max)', C.particle, { min: 0, step: 0.01, keyByDefault: false }),
    scalar('particle.rotationSpeedMin', 'Spin (min)', C.particle, { step: 1, keyByDefault: false }),
    scalar('particle.rotationSpeedMax', 'Spin (max)', C.particle, { step: 1, keyByDefault: false }),
    vector('particle.gravity', 'Gravity', 'particle.gravity', C.particle, { step: 0.1, keyByDefault: false }),
    // Multipliers of the system's own colour / fade-over-lifetime, so particles keep fading out.
    scalar('particle.opacity', 'Particle opacity', C.opacity, { min: 0, max: 1 }),
    { ...colorGroup('particle.tint', 'Particle tint', 'particle.tint', false), max: 2 },
];

const FOG_GROUPS: AnimationPropertyGroup[] = [
    scalar('fog.density', 'Density', C.scene, { min: 0, max: 0.1, step: 0.001 }),
    scalar('fog.start', 'Start distance', C.scene, { min: 0, step: 0.5 }),
    scalar('fog.end', 'End distance', C.scene, { min: 0, step: 0.5 }),
    colorGroup('fog.color', 'Color', 'fog.color'),
];

const WEATHER_GROUPS: AnimationPropertyGroup[] = [
    scalar('weather.speed', 'Fall speed', C.scene, { min: 0, step: 0.1 }),
    scalar('weather.drift', 'Drift', C.scene, { min: 0, step: 0.1 }),
    scalar('weather.opacity', 'Opacity', C.opacity, { min: 0, max: 1 }),
    scalar('weather.elongate', 'Elongation', C.scene, { min: 1, max: 20, step: 0.1 }),
    scalar('weather.particleMinSize', 'Min size', C.scene, { min: 0, step: 0.001 }),
    scalar('weather.particleMaxSize', 'Max size', C.scene, { min: 0, step: 0.001 }),
    colorGroup('weather.color', 'Color', 'weather.color'),
];

const WIND_GROUPS: AnimationPropertyGroup[] = [
    scalar('wind.speed', 'Speed', C.scene, { min: 0, step: 0.1 }),
    scalar('wind.direction', 'Direction', C.scene, { step: 1, unit: '°' }),
    scalar('wind.gustiness', 'Gustiness', C.scene, { min: 0, max: 1, step: 0.01 }),
    scalar('wind.turbulence', 'Turbulence', C.scene, { min: 0, max: 1, step: 0.01, keyByDefault: false }),
];

const FOCUS_GROUPS: AnimationPropertyGroup[] = [
    // Labels match the Focus and highlight panel.
    colorGroup('focus.tint', 'Highlight color', 'focus.tint'),
    scalar('focus.tintStrength', 'Highlight tint', C.scene, { min: 0, max: 1 }),
    scalar('focus.outsideBrightness', 'Surrounding brightness', C.scene, { min: 0, max: 1 }),
    scalar('focus.outsideSaturation', 'Surrounding saturation', C.scene, { min: 0, max: 1 }),
];

const HAZE_GROUPS: AnimationPropertyGroup[] = [
    scalar('haze.amount', 'Haze amount', C.scene, { min: 0, max: 1 }),
    colorGroup('haze.color', 'Haze color', 'haze.color'),
];

const RELIGHTING_GROUPS: AnimationPropertyGroup[] = [
    scalar('relighting.ambientIntensity', 'Ambient intensity', C.light, { min: 0, max: 3 }),
    colorGroup('relighting.ambientColor', 'Ambient color', 'relighting.ambientColor'),
    scalar('relighting.shadowIntensity', 'Shadow strength', C.scene, { min: 0, max: 1, keyByDefault: false }),
];

const ENVIRONMENT_GROUPS: AnimationPropertyGroup[] = [
    // Stored in radians like the Skybox panel's slider; shown in degrees.
    scalar('skybox.rotation', 'Skybox rotation', C.rotation, { min: 0, max: 360, step: 1, unit: '°', displayScale: 180 / Math.PI }),
    colorGroup('background.color', 'Background color', 'background.color'),
];

const AUDIO_REACTIVE_GROUPS: AnimationPropertyGroup[] = [
    scalar('audioReactive.master', 'Master intensity', C.audio, { min: 0, max: 2 }),
    scalar('audioReactive.pulse', 'Bass pulse', C.audio, { min: 0, max: 1 }),
    scalar('audioReactive.wave', 'Wave ripple', C.audio, { min: 0, max: 1 }),
    scalar('audioReactive.wobble', 'Wobble / warp', C.audio, { min: 0, max: 1 }),
    scalar('audioReactive.hueShift', 'Hue shift', C.audio, { min: 0, max: 1 }),
    scalar('audioReactive.waveScale', 'Ripple scale', C.audio, { min: 0.05, max: 3, step: 0.05, keyByDefault: false }),
    scalar('audioReactive.waveSpeed', 'Ripple speed', C.audio, { min: 0.5, max: 12, step: 0.1, keyByDefault: false }),
    scalar('audioReactive.hueSpeed', 'Hue speed', C.audio, { min: 0, max: 1, keyByDefault: false }),
];

const POST_GROUPS: AnimationPropertyGroup[] = [
    scalar('post.exposure', 'Exposure', C.light, { min: 0, max: 5 }),
    scalar('post.grading.brightness', 'Brightness', C.light, { min: 0, max: 3 }),
    scalar('post.grading.contrast', 'Contrast', C.scene, { min: 0.5, max: 1.5 }),
    scalar('post.grading.saturation', 'Saturation', C.scene, { min: 0, max: 2 }),
    colorGroup('post.grading.tint', 'Color tint', 'post.grading.tint'),
    scalar('post.colorEnhance.shadows', 'Shadows', C.scene, { min: -3, max: 3 }),
    scalar('post.colorEnhance.highlights', 'Highlights', C.scene, { min: -3, max: 3 }),
    scalar('post.colorEnhance.midtones', 'Midtones', C.scene, { min: -1, max: 1 }),
    scalar('post.colorEnhance.vibrance', 'Vibrance', C.scene, { min: -1, max: 1 }),
    scalar('post.colorEnhance.dehaze', 'Dehaze', C.scene, { min: -1, max: 1 }),
    scalar('post.bloom.intensity', 'Bloom', C.light, { min: 0, max: 0.1, step: 0.001 }),
    scalar('post.vignette.intensity', 'Vignette', C.scene, { min: 0, max: 1 }),
    scalar('post.vignette.inner', 'Vignette inner', C.scene, { min: 0, max: 3 }),
    scalar('post.vignette.outer', 'Vignette outer', C.scene, { min: 0, max: 3 }),
    scalar('post.vignette.curvature', 'Vignette curvature', C.scene, { min: 0.01, max: 10, step: 0.1 }),
    colorGroup('post.vignette.color', 'Vignette color', 'post.vignette.color'),
    scalar('post.fringing.intensity', 'Chromatic aberration', C.scene, { min: 0, max: 100, step: 1 }),
    scalar('post.sharpness', 'Sharpness', C.scene, { min: 0, max: 1 }),
];
// Post effects are opt-in per control (a "Key all" over twenty sliders is never what anyone wants).
for (const group of POST_GROUPS) group.keyByDefault = false;

// Region values are in the region panel's units: scene coordinates, rotation in degrees,
// full size, feather as a fraction of the half-size.
const REGION_GROUPS: AnimationPropertyGroup[] = [
    vector('region.position', 'Position', 'region.position', C.position, { step: 0.05 }),
    vector('region.rotation', 'Rotation', 'region.rotation', C.rotation, { step: 1, unit: '°' }),
    vector('region.size', 'Size', 'region.size', C.scale, { step: 0.05, min: 0.01 }),
    scalar('region.feather', 'Edge softness', C.scene, { min: 0, max: 1, keyByDefault: false }),
];

// One focus region: its transform plus its own highlight colour (the shared tint strength and
// surroundings stay on the scene-wide Focus & Highlights target).
const FOCUS_REGION_GROUPS: AnimationPropertyGroup[] = [...REGION_GROUPS, colorGroup('region.tint', 'Highlight color', 'region.tint', false)];

// One annotation layer: its colour and strength (the editor's Strength slider). The outlines
// themselves (shape, depth, soft edge) are compiled into the splat shader and stay fixed.
const ANNOTATION_LAYER_GROUPS: AnimationPropertyGroup[] = [
    colorGroup('annotation.color', 'Layer color', 'annotation.color'),
    scalar('annotation.opacity', 'Layer strength', C.opacity, { min: 0, max: 1 }),
];

// One region (labels match the Regions panel): its colour and colour strength, the look of the
// rest while it dims it, and for a region whose only area is a sphere / box, that area. The
// areas' shapes otherwise are compiled into the splat shader and stay fixed.
const REGION_LOOK_GROUPS: AnimationPropertyGroup[] = [
    colorGroup('region.color', 'Colour', 'region.color'),
    scalar('region.colorStrength', 'Colour strength', C.opacity, { min: 0, max: 1 }),
];
const REGION_DIM_GROUPS: AnimationPropertyGroup[] = [
    scalar('region.dimBrightness', 'Brightness of the rest', C.scene, { min: 0, max: 1 }),
    scalar('region.dimSaturation', 'Colour of the rest', C.scene, { min: 0, max: 1 }),
];

export interface AnimationTargetContext {
    /** Light type ('point' | 'spot' | 'directional' | 'hemispheric') — decides rotation/range/cone. */
    lightType?: string;
    /** Hotspot / portal marker type ('sphere' | 'image' | …) — only spheres have a color. Unknown = sphere. */
    markerType?: string;
    /** Regions: false when the region doesn't dim the rest (no brightness / saturation of the rest). Unknown = it does. */
    regionDims?: boolean;
    /**
     * Regions: false unless the region's only area is a sphere / box (regionAnimatableVolume), the
     * area its position / rotation / size / softness keys move. Unknown = it is.
     */
    regionVolume?: boolean;
}

/** The animatable property groups of a target type, in timeline order. */
export function getAnimationPropertyGroups(entityType: AnimatableEntityType | string, context: AnimationTargetContext = {}): AnimationPropertyGroup[] {
    switch (entityType) {
        case 'hotspot':
        case 'portal':
            return context.markerType && context.markerType !== 'sphere'
                ? [POSITION, ROTATION, SCALE, OPACITY, VISIBLE]
                : [POSITION, ROTATION, SCALE, OPACITY, MARKER_COLOR, VISIBLE];
        case 'customMesh':
            return [POSITION, ROTATION, SCALE, OPACITY, VISIBLE];
        case 'htmlMesh':
            return [POSITION, ROTATION, SCALE, VISIBLE];
        case 'mirrorPlane':
            return [POSITION, ROTATION, SCALE, ...MIRROR_GROUPS, VISIBLE];
        case 'waterPlane':
            return [POSITION, ROTATION, SCALE, ...WATER_GROUPS, VISIBLE];
        case 'cloudLayer':
            return [POSITION, ROTATION, SCALE, ...CLOUD_GROUPS, VISIBLE];
        case 'light': {
            const type = context.lightType;
            const groups: AnimationPropertyGroup[] = [POSITION];
            if (type !== 'point') groups.push(ROTATION);
            groups.push(colorGroup('light.color', 'Color', 'light.color'));
            groups.push(scalar('light.intensity', 'Intensity', C.light, { min: 0, step: 0.05 }));
            if (type !== 'directional' && type !== 'hemispheric') {
                groups.push(scalar('light.range', 'Range', C.light, { min: 0, step: 0.1, keyByDefault: false }));
            }
            if (type === 'spot' || type === undefined) {
                groups.push(scalar('light.coneAngle', 'Cone angle', C.light, { min: 1, max: 90, step: 0.5, unit: '°', keyByDefault: false }));
            }
            groups.push(VISIBLE);
            return groups;
        }
        case 'particle':
            return [POSITION, ROTATION, SCALE, ...PARTICLE_GROUPS, VISIBLE];
        case 'audioEmitter':
            return [POSITION, scalar('audio.volume', 'Volume', C.audio, { min: 0, max: 1 })];
        case 'fog':
            return FOG_GROUPS;
        case 'weather':
            return WEATHER_GROUPS;
        case 'wind':
            return WIND_GROUPS;
        case 'focus':
            return FOCUS_GROUPS;
        case 'haze':
            return HAZE_GROUPS;
        case 'relighting':
            return RELIGHTING_GROUPS;
        case 'postProcessing':
            return POST_GROUPS;
        case 'focusRegion':
            return FOCUS_REGION_GROUPS;
        case 'hazeRegion':
            return REGION_GROUPS;
        case 'annotationLayer':
            return ANNOTATION_LAYER_GROUPS;
        case 'region':
            return [
                ...REGION_LOOK_GROUPS,
                ...(context.regionDims === false ? [] : REGION_DIM_GROUPS),
                ...(context.regionVolume === false ? [] : REGION_GROUPS),
            ];
        case 'environment':
            return ENVIRONMENT_GROUPS;
        case 'audioReactive':
            return AUDIO_REACTIVE_GROUPS;
        default:
            return [POSITION, ROTATION, SCALE];
    }
}

/** The group a channel belongs to for a target type (falls back to a one-channel group). */
export function findAnimationPropertyGroup(entityType: AnimatableEntityType | string, property: string, context: AnimationTargetContext = {}): AnimationPropertyGroup {
    const groups = getAnimationPropertyGroups(entityType, context);
    const found = groups.find((g) => (g.channels as readonly string[]).includes(property));
    if (found) return found;
    // Any catalog (a light property on a light whose type changed, an older/newer scene field).
    for (const type of ['light', 'particle', 'audioEmitter', 'fog', 'weather', 'wind', 'region', 'focus', 'haze', 'focusRegion', 'relighting', 'postProcessing', 'environment', 'audioReactive', 'annotationLayer', 'hotspot', 'mirrorPlane', 'waterPlane', 'cloudLayer']) {
        const g = getAnimationPropertyGroups(type, {}).find((x) => (x.channels as readonly string[]).includes(property));
        if (g) return g;
    }
    return { id: property, label: property, kind: 'scalar', color: '#ffb800', keyByDefault: false, channels: [property as AnimatableProperty] };
}

/** Properties evaluated as a hold (no interpolation) whatever their easing: on/off switches. */
export function isStepAnimationProperty(property: string): boolean {
    return property === 'visible';
}

// ── Colors ────────────────────────────────────────────────────────────────────────────────

/** '#rrggbb' (or 'rrggbb' / '#rgb') → 0-1 channels; null when it is not a hex color. */
export function hexToAnimationRgb(hex: string | undefined | null): { r: number; g: number; b: number } | null {
    if (typeof hex !== 'string') return null;
    let h = hex.trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(h)) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-f]{6}/i.test(h)) return null;
    return {
        r: parseInt(h.slice(0, 2), 16) / 255,
        g: parseInt(h.slice(2, 4), 16) / 255,
        b: parseInt(h.slice(4, 6), 16) / 255,
    };
}

/** 0-1 channels → '#rrggbb' (clamped). */
export function animationRgbToHex(r: number, g: number, b: number): string {
    const to = (v: number) => {
        const n = Math.round(Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0)) * 255);
        return n.toString(16).padStart(2, '0');
    };
    return `#${to(r)}${to(g)}${to(b)}`;
}

// ── Rotation continuity ───────────────────────────────────────────────────────────────────

const TWO_PI = Math.PI * 2;
const nearestWrap = (value: number, reference: number) => value + TWO_PI * Math.round((reference - value) / TWO_PI);

/**
 * The Euler triple (radians, stored keyframe convention) for the same orientation as `target`
 * that is closest to `reference`. Captured rotations come from the engine's Euler
 * decomposition, which can flip between equivalent forms ((x, y, z) and (x+π, π−y, z+π)) or
 * wrap at ±180°; interpolating between such keyframes would spin the long way round. Keying a
 * rotation next to an existing keyframe picks the closest form instead.
 */
export function closestEquivalentRotation(
    target: { x: number; y: number; z: number },
    reference: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
    const candidates = [
        { x: target.x, y: target.y, z: target.z },
        { x: target.x + Math.PI, y: Math.PI - target.y, z: target.z + Math.PI },
    ].map((c) => ({ x: nearestWrap(c.x, reference.x), y: nearestWrap(c.y, reference.y), z: nearestWrap(c.z, reference.z) }));
    let best = candidates[0];
    let bestDistance = Infinity;
    for (const c of candidates) {
        const d = Math.abs(c.x - reference.x) + Math.abs(c.y - reference.y) + Math.abs(c.z - reference.z);
        if (d < bestDistance - 1e-9) {
            best = c;
            bestDistance = d;
        }
    }
    return best;
}
